import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button, Container } from 'react-bootstrap';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import { withTranslation } from 'react-i18next';
import type { WithTranslation } from 'react-i18next';

interface OwnProps {
  children: ReactNode;
}

type Props = OwnProps & WithTranslation;

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    const { t } = this.props;

    if (this.state.hasError) {
      const isDevelopment = import.meta.env.DEV;
      const isHMRError = this.state.error?.message?.includes('useAuth must be used within') ||
                         this.state.error?.message?.includes('Context') ||
                         this.state.error?.message?.includes('Provider');

      return (
        <div
          className="an-enterprise-page"
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem 1rem',
            background:
              'radial-gradient(circle at 50% 24%, rgb(var(--an-error-rgb) / 0.10), var(--an-transparent) 24rem), var(--an-background)',
          }}
        >
          <Container style={{ maxWidth: 720 }}>
            <section
              className="an-panel"
              role="alert"
              aria-live="assertive"
              style={{
                padding: '2rem',
                textAlign: 'center',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  margin: '0 auto 1rem',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'var(--an-error)',
                  background: 'var(--an-error-light)',
                  borderRadius: 'var(--an-radius-md)',
                  boxShadow: '0 0 0 8px rgb(var(--an-error-rgb) / 0.06)',
                }}
              >
                <ErrorOutline fontSize="large" />
              </div>

              <p
                style={{
                  margin: '0 0 0.5rem',
                  color: 'var(--an-error)',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                }}
              >
                AttendNow
              </p>

              <h1
                className="an-page-title"
                style={{
                  margin: 0,
                  fontSize: 'clamp(1.65rem, 3vw, 2.35rem)',
                  lineHeight: 1.15,
                }}
              >
                {isDevelopment && isHMRError ? t('hmrTitle') : t('errorTitle')}
              </h1>

              <p
                className="an-page-subtitle"
                style={{
                  maxWidth: 560,
                  margin: '0.85rem auto 0',
                  fontSize: '1rem',
                }}
              >
                {isDevelopment && isHMRError ? t('hmrMessage') : t('errorMessage')}
              </p>

              {isDevelopment && (
                <details
                  style={{
                    marginTop: '1.5rem',
                    textAlign: 'left',
                    background: 'var(--an-surface-muted)',
                    border: '1px solid var(--an-border-light)',
                    borderRadius: 'var(--an-radius-md)',
                    padding: '1rem',
                  }}
                >
                  <summary
                    style={{
                      cursor: 'pointer',
                      color: 'var(--an-text-primary)',
                      fontWeight: 800,
                    }}
                  >
                    {t('errorDetails')}
                  </summary>
                  <pre
                    style={{
                      margin: '0.75rem 0 0',
                      maxHeight: 260,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: 'var(--an-text-secondary)',
                      background: 'var(--an-surface)',
                      border: '1px solid var(--an-border-light)',
                      borderRadius: 'var(--an-radius-sm)',
                      padding: '0.875rem',
                      fontSize: '0.8125rem',
                    }}
                  >
                    {this.state.error?.message}
                    {'\n\n'}
                    {this.state.error?.stack}
                  </pre>
                </details>
              )}

              <div
                style={{
                  marginTop: '1.5rem',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Button variant="primary" onClick={this.handleReload}>
                  <Refresh fontSize="small" />
                  {t('refreshPage')}
                </Button>
              </div>
            </section>
          </Container>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withTranslation('components', { keyPrefix: 'errorBoundary' })(ErrorBoundary);
